// File: src/hooks/useHistory.js
import { useState, useEffect, useCallback } from 'react';
import { fetchHistory } from '../api/api';
import Cookies from 'js-cookie';

const CACHE_EXPIRATION_TIME = 5 * 60 * 1000; // 5 minutes in milliseconds
const HISTORY_CACHE_KEY = 'history_data_cache';

export const useHistory = () => {
  const [historyList, setHistoryList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load from localStorage on initial render
  useEffect(() => {
    const cachedData = localStorage.getItem(HISTORY_CACHE_KEY);
    if (cachedData) {
      try {
        const { data, timestamp } = JSON.parse(cachedData);
        const isExpired = Date.now() - timestamp > CACHE_EXPIRATION_TIME;
        
        if (!isExpired && data.length > 0) {
          setHistoryList(data);
          return; // Don't fetch if we have valid cached data
        }
      } catch (err) {
        console.error('Error parsing cached history:', err);
        // Continue to fetch if parse fails
      }
    }
    
    // Fetch on mount if no valid cache exists
    fetchHistoryData();
  }, []);

  const fetchHistoryData = useCallback(async (forceRefresh = false) => {
    const cachedData = localStorage.getItem(HISTORY_CACHE_KEY);
    
    // Use cache if available and not forcing refresh
    if (!forceRefresh && cachedData) {
      try {
        const { data, timestamp } = JSON.parse(cachedData);
        const isExpired = Date.now() - timestamp > CACHE_EXPIRATION_TIME;
        
        if (!isExpired) {
          setHistoryList(data);
          return;
        }
      } catch (err) {
        console.error('Error using cached history:', err);
        // Continue to fetch if using cache fails
      }
    }

    try {
      setIsLoading(true);
      setError(null);
      const authToken = Cookies.get('authToken');
      
      if (!authToken) {
        setHistoryList([]);
        setIsLoading(false);
        return;
      }
      
      const results = await fetchHistory(authToken);
      
      // Update state
      setHistoryList(results);
      
      // Cache the results
      const cacheData = {
        data: results,
        timestamp: Date.now()
      };
      localStorage.setItem(HISTORY_CACHE_KEY, JSON.stringify(cacheData));
      
    } catch (err) {
      console.error('Error fetching history:', err);
      setError('Failed to load search history');
      setHistoryList([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    historyList,
    isLoading,
    error,
    refreshHistory: (force = true) => fetchHistoryData(force)
  };
};